// useLearningJourneys.ts
// ======================
// Custom hook for managing learning journeys with Firebase CRUD operations
// Feature #21 - Implements Firebase CRUD for læringsforløb

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  createLearningJourney,
  getLearningJourneys,
  getLearningJourney,
  updateLearningJourney,
  deleteLearningJourney,
} from '../services/firebase';
import i18n from '../i18n';
import type { LearningJourney, ExamFormat } from '../types';

// Helper to convert Firebase Timestamp to Date/string for consistent handling
function normalizeJourneyDates(journey: Record<string, unknown>): LearningJourney {
  const normalized = { ...journey } as unknown as LearningJourney;

  // Handle examDate conversion
  if (journey.examDate) {
    if (typeof journey.examDate === 'object' && 'toDate' in (journey.examDate as object)) {
      // Firebase Timestamp
      normalized.examDate = (journey.examDate as { toDate: () => Date }).toDate().toISOString();
    } else if (journey.examDate instanceof Date) {
      normalized.examDate = journey.examDate.toISOString();
    }
  }

  // Handle createdAt conversion
  if (journey.createdAt) {
    if (typeof journey.createdAt === 'object' && 'toDate' in (journey.createdAt as object)) {
      normalized.createdAt = (journey.createdAt as { toDate: () => Date }).toDate().toISOString();
    } else if (journey.createdAt instanceof Date) {
      normalized.createdAt = journey.createdAt.toISOString();
    }
  }

  // Handle updatedAt conversion
  if (journey.updatedAt) {
    if (typeof journey.updatedAt === 'object' && 'toDate' in (journey.updatedAt as object)) {
      normalized.updatedAt = (journey.updatedAt as { toDate: () => Date }).toDate().toISOString();
    } else if (journey.updatedAt instanceof Date) {
      normalized.updatedAt = journey.updatedAt.toISOString();
    }
  }

  return normalized;
}

export interface UseLearningJourneysResult {
  // Data
  journeys: LearningJourney[];
  loading: boolean;
  error: string | null;

  // Actions
  fetchJourneys: () => Promise<void>;
  createJourney: (data: CreateJourneyInput) => Promise<{ success: boolean; id?: string; error?: string }>;
  updateJourney: (journeyId: string, data: UpdateJourneyInput) => Promise<{ success: boolean; error?: string }>;
  deleteJourney: (journeyId: string) => Promise<{ success: boolean; error?: string }>;
  getJourney: (journeyId: string) => Promise<LearningJourney | null>;

  // Utilities
  getJourneyById: (journeyId: string) => LearningJourney | undefined;
}

export interface CreateJourneyInput {
  subject: string;
  title?: string;
  examDate?: Date | string;
  examFormat?: ExamFormat;
  weeklyStudyHours?: number;
}

export interface UpdateJourneyInput {
  subject?: string;
  title?: string;
  examDate?: Date | string | null;
  examFormat?: ExamFormat;
  weeklyStudyHours?: number;
  topicIds?: string[];
  analyzedMaterialIds?: string[]; // Feature #180: Track which materials were analyzed
  status?: 'active' | 'completed' | 'archived';
}

export function useLearningJourneys(): UseLearningJourneysResult {
  const { user, isMockAuth } = useAuth();
  const [journeys, setJourneys] = useState<LearningJourney[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // LocalStorage key for mock auth fallback
  const localStorageKey = user?.uid ? `lyra_journeys_${user.uid}` : 'lyra_journeys';

  // Fetch all journeys for the current user
  const fetchJourneys = useCallback(async () => {
    if (!user?.uid) {
      setJourneys([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isMockAuth) {
        // Use localStorage for mock auth (development)
        const saved = localStorage.getItem(localStorageKey);
        const parsed = saved ? JSON.parse(saved) : [];
        setJourneys(parsed);
      } else {
        // Use Firebase for real auth
        const result = await getLearningJourneys(user.uid);
        if (result.success && result.data) {
          // Normalize dates from Firebase Timestamps
          const normalized = result.data.map((j: Record<string, unknown>) => normalizeJourneyDates(j));
          setJourneys(normalized);
        } else {
          setError(result.error || 'Kunne ikke hente læringsforløb');
          setJourneys([]);
        }
      }
    } catch (err) {
      console.error('[useLearningJourneys] Error fetching journeys:', err);
      setError(i18n.t('errors:fetch.journeys'));
      setJourneys([]);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, isMockAuth, localStorageKey]);

  // Create a new journey
  const createJourney = useCallback(async (data: CreateJourneyInput): Promise<{ success: boolean; id?: string; error?: string }> => {
    if (!user?.uid) {
      return { success: false, error: 'Ingen bruger logget ind' };
    }

    try {
      if (isMockAuth) {
        // Mock auth: use localStorage
        const newJourney: LearningJourney = {
          id: `journey_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          userId: user.uid,
          subject: data.subject,
          title: data.title || data.subject,
          examDate: data.examDate instanceof Date ? data.examDate.toISOString() : data.examDate,
          examFormat: data.examFormat,
          weeklyStudyHours: data.weeklyStudyHours,
          topicIds: [],
          status: 'active',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const existingJourneys = JSON.parse(localStorage.getItem(localStorageKey) || '[]');
        existingJourneys.push(newJourney);
        localStorage.setItem(localStorageKey, JSON.stringify(existingJourneys));

        // Update local state
        setJourneys(existingJourneys);

        return { success: true, id: newJourney.id };
      } else {
        // Firebase: use CRUD function
        const result = await createLearningJourney({
          userId: user.uid,
          subject: data.subject,
          title: data.title || data.subject,
          examDate: data.examDate,
          examFormat: data.examFormat,
          weeklyStudyHours: data.weeklyStudyHours,
        });

        if (result.success && result.id) {
          // Refresh journeys list
          await fetchJourneys();
          return { success: true, id: result.id };
        } else {
          return { success: false, error: result.error || 'Kunne ikke oprette læringsforløb' };
        }
      }
    } catch (err) {
      console.error('[useLearningJourneys] Error creating journey:', err);
      return { success: false, error: 'Der opstod en fejl ved oprettelse af læringsforløb' };
    }
  }, [user?.uid, isMockAuth, localStorageKey, fetchJourneys]);

  // Update an existing journey
  const updateJourney = useCallback(async (journeyId: string, data: UpdateJourneyInput): Promise<{ success: boolean; error?: string }> => {
    if (!user?.uid) {
      return { success: false, error: 'Ingen bruger logget ind' };
    }

    try {
      if (isMockAuth) {
        // Mock auth: update in localStorage
        const existingJourneys = JSON.parse(localStorage.getItem(localStorageKey) || '[]');
        const index = existingJourneys.findIndex((j: LearningJourney) => j.id === journeyId);

        if (index === -1) {
          return { success: false, error: 'Læringsforløb ikke fundet' };
        }

        const updated = {
          ...existingJourneys[index],
          ...data,
          examDate: data.examDate instanceof Date ? data.examDate.toISOString() : data.examDate,
          updatedAt: new Date().toISOString(),
        };
        existingJourneys[index] = updated;
        localStorage.setItem(localStorageKey, JSON.stringify(existingJourneys));

        // Update local state
        setJourneys(existingJourneys);

        return { success: true };
      } else {
        // Firebase: use CRUD function
        const result = await updateLearningJourney(journeyId, data);

        if (result.success) {
          // Refresh journeys list
          await fetchJourneys();
          return { success: true };
        } else {
          return { success: false, error: result.error || 'Kunne ikke opdatere læringsforløb' };
        }
      }
    } catch (err) {
      console.error('[useLearningJourneys] Error updating journey:', err);
      return { success: false, error: 'Der opstod en fejl ved opdatering af læringsforløb' };
    }
  }, [user?.uid, isMockAuth, localStorageKey, fetchJourneys]);

  // Delete a journey
  const deleteJourney = useCallback(async (journeyId: string): Promise<{ success: boolean; error?: string }> => {
    if (!user?.uid) {
      return { success: false, error: 'Ingen bruger logget ind' };
    }

    try {
      if (isMockAuth) {
        // Mock auth: remove from localStorage
        const existingJourneys = JSON.parse(localStorage.getItem(localStorageKey) || '[]');
        const filtered = existingJourneys.filter((j: LearningJourney) => j.id !== journeyId);
        localStorage.setItem(localStorageKey, JSON.stringify(filtered));

        // Update local state
        setJourneys(filtered);

        return { success: true };
      } else {
        // Firebase: use CRUD function
        const result = await deleteLearningJourney(journeyId);

        if (result.success) {
          // Refresh journeys list
          await fetchJourneys();
          return { success: true };
        } else {
          return { success: false, error: result.error || 'Kunne ikke slette læringsforløb' };
        }
      }
    } catch (err) {
      console.error('[useLearningJourneys] Error deleting journey:', err);
      return { success: false, error: 'Der opstod en fejl ved sletning af læringsforløb' };
    }
  }, [user?.uid, isMockAuth, localStorageKey, fetchJourneys]);

  // Get a single journey by ID (fetches fresh data)
  const getJourney = useCallback(async (journeyId: string): Promise<LearningJourney | null> => {
    if (!user?.uid) {
      return null;
    }

    try {
      if (isMockAuth) {
        // Mock auth: get from localStorage
        const existingJourneys = JSON.parse(localStorage.getItem(localStorageKey) || '[]');
        return existingJourneys.find((j: LearningJourney) => j.id === journeyId) || null;
      } else {
        // Firebase: use CRUD function
        const result = await getLearningJourney(journeyId);
        if (result.success && result.data) {
          return normalizeJourneyDates(result.data as Record<string, unknown>);
        }
        return null;
      }
    } catch (err) {
      console.error('[useLearningJourneys] Error getting journey:', err);
      return null;
    }
  }, [user?.uid, isMockAuth, localStorageKey]);

  // Get a journey from the cached list (no network call)
  const getJourneyById = useCallback((journeyId: string): LearningJourney | undefined => {
    return journeys.find(j => j.id === journeyId);
  }, [journeys]);

  // Fetch journeys on mount and when user changes
  useEffect(() => {
    fetchJourneys();
  }, [fetchJourneys]);

  return {
    journeys,
    loading,
    error,
    fetchJourneys,
    createJourney,
    updateJourney,
    deleteJourney,
    getJourney,
    getJourneyById,
  };
}

export default useLearningJourneys;
